import { useRef } from "react";
import { agencyIntroSections, type AgencyPolicyBlock } from "./agency-policy-content";
import { agencyRewardSections } from "./agency-policy-rewards";
import { ArrowUp, ChevronRight } from "lucide-react";
import { AgencyPolicyVisual, policyChapterArt } from "./AgencyPolicyVisual";
import "./agency-policy.css";

const sections = [...agencyIntroSections, ...agencyRewardSections];

function PolicyBlock({ block }: { block: AgencyPolicyBlock }) {
  if (block.type === "table") return <div className="agency-policy__table">
    <table>
      <caption>{block.caption}</caption>
      <thead><tr>{block.columns.map(column => <th key={column} scope="col">{column}</th>)}</tr></thead>
      <tbody>{block.rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => cellIndex === 0
        ? <th key={cellIndex} scope="row">{cell}</th>
        : <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody>
    </table>
    {block.note && <p className="agency-policy__table-note">{block.note}</p>}
  </div>;
  if (block.type === "heading") return <h4>{block.text}</h4>;
  if (block.type === "list") {
    const List = block.ordered ? "ol" : "ul";
    return <List className={block.emphasis ? "agency-policy__emphasis" : undefined}>{block.items.map((text, index) => <li key={index}>{text}</li>)}</List>;
  }
  return <p className={block.emphasis ? "agency-policy__emphasis" : undefined}>{block.text}</p>;
}

export function AgencyPolicy() {
  const headings = useRef<Record<string, HTMLHeadingElement | null>>({});
  const contents = useRef<HTMLHeadingElement | null>(null);
  function goToSection(id: string) {
    const heading = headings.current[id];
    heading?.focus({ preventScroll: true });
    heading?.scrollIntoView({ block: "start", behavior: "instant" });
  }
  function goToContents() {
    contents.current?.focus({ preventScroll: true });
    contents.current?.scrollIntoView({ block: "start", behavior: "instant" });
  }
  return <article className="agency-policy" aria-label="代理制度正文">
    <header className="agency-policy__intro">
      <div className="agency-policy__hero">
        <img src="/assets/west-coast/agency-partnership-hero-v1.webp" alt="" width="1280" height="721" />
        <div className="agency-policy__hero-copy"><p>合营计划＋团队提成</p><h2 ref={contents} tabIndex={-1}>代理制度</h2><span>五项计划，图文读懂</span></div>
      </div>
      <aside className="agency-policy__status" aria-label="代理结算当前状态">
        <strong>月度合营佣金自动结算已启用</strong>
        <p>系统按北京时间归集直属会员数据，并在次月 7 日 18:00 后于数据覆盖完整时自动结算。实际档位、金额与入账状态以佣金报表为准。</p>
      </aside>
      <nav aria-label="代理制度目录">{sections.map(section => {
        const art = policyChapterArt[section.id as keyof typeof policyChapterArt];
        return <button key={section.id} type="button" aria-label={section.title} onClick={() => goToSection(section.id)}><art.Icon aria-hidden="true" /><span><strong>{art.label}</strong><small>{art.hint}</small></span><ChevronRight aria-hidden="true" /></button>;
      })}</nav>
    </header>
    {sections.map((section, index) => <section key={section.id} className="referral-card agency-policy__section" aria-labelledby={`agency-policy-${section.id}`}>
      <h3 id={`agency-policy-${section.id}`} tabIndex={-1} ref={node => { headings.current[section.id] = node; }}><span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>{section.title}</h3>
      <AgencyPolicyVisual section={section} />
      {section.blocks.map((block, blockIndex) => <PolicyBlock key={blockIndex} block={block} />)}
      <button className="agency-policy__back" type="button" onClick={goToContents}><ArrowUp aria-hidden="true" />返回目录</button>
    </section>)}
  </article>;
}
